import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Loader2, Send, AlertTriangle, Plus } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable "Send Job Confirmation" modal.
 * Props:
 *   - open, onOpenChange
 *   - jobId
 *   - api        axios instance (from useAuth or useMobileAuth)
 *   - onSent()   optional callback after a successful send
 */
export const SendJobConfirmationModal = ({ open, onOpenChange, jobId, api, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [options, setOptions] = useState([]); // {email, label, checked}
  const [manualEmail, setManualEmail] = useState('');
  const [alreadySent, setAlreadySent] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/jobs/${jobId}/confirmation-preview`);
      setSubject(res.data.subject || '');
      setBody(res.data.body || '');
      setAlreadySent(!!res.data.already_sent);

      const r = res.data.recipients || {};
      const opts = [];
      const seen = new Set();
      const add = (email, label, checked) => {
        const e = (email || '').trim().toLowerCase();
        if (!e || seen.has(e)) return;
        seen.add(e);
        opts.push({ email: e, label, checked });
      };
      if (r.customer_email) add(r.customer_email, 'Customer', true);
      (r.customer_contacts || []).forEach((c) => add(c.email, `Customer contact: ${c.name || c.email}`, !!c.is_primary));
      (r.site_contacts || []).forEach((c) => add(c.email, `Site contact: ${c.name || c.email}`, false));
      setOptions(opts);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load confirmation preview');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [api, jobId, onOpenChange]);

  useEffect(() => {
    if (open && jobId) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const toggle = (email) => {
    setOptions((prev) => prev.map((o) => (o.email === email ? { ...o, checked: !o.checked } : o)));
  };

  const addManual = () => {
    const e = manualEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (options.some((o) => o.email === e)) {
      toast.info('That email is already in the list');
      setManualEmail('');
      return;
    }
    setOptions((prev) => [...prev, { email: e, label: 'Added manually', checked: true }]);
    setManualEmail('');
  };

  const handleSend = async () => {
    const recipients = options.filter((o) => o.checked).map((o) => o.email);
    if (recipients.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/jobs/${jobId}/send-confirmation`, {
        recipients,
        subject,
        body,
        set_confirmed: true,
      });
      const failed = res.data.failed || [];
      if (failed.length) {
        toast.warning(`Sent to ${res.data.sent.length}, failed for ${failed.length}`);
      } else {
        toast.success(`Confirmation sent to ${res.data.sent.length} recipient(s)`);
      }
      onSent?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send confirmation');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-confirmation-modal">
        <DialogHeader>
          <DialogTitle>Send Job Confirmation</DialogTitle>
          <DialogDescription>Review the recipients and message, then send. You can edit before sending.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {alreadySent && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-600" data-testid="already-sent-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>A confirmation has already been sent for this job. Sending again will notify the customer once more.</span>
              </div>
            )}

            <div className="space-y-2">
              <Label className="label-overline">Recipients</Label>
              {options.length === 0 ? (
                <p className="text-sm text-muted-foreground">No saved emails found — add one below.</p>
              ) : (
                <div className="space-y-2">
                  {options.map((o) => (
                    <label key={o.email} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`recipient-${o.email}`}>
                      <Checkbox checked={o.checked} onCheckedChange={() => toggle(o.email)} />
                      <span className="font-medium">{o.email}</span>
                      <span className="text-muted-foreground text-xs">({o.label})</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Input
                  type="email"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }}
                  placeholder="Add another email…"
                  data-testid="manual-email-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={addManual} data-testid="add-manual-email-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="confirmation-subject-input" />
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="text-sm" data-testid="confirmation-body-input" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={loading || sending} data-testid="confirm-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendJobConfirmationModal;
